var Background           = require("./Background"),
    Control              = require("enyo/Control"),
    FloatingSupport      = require("enyo-ui/FloatingSupport"),
    FadeSupport          = require("enyo-ui/FadeSupport"),
    MediaDetailsPopup    = require("./MediaDetailsPopup"),
    Scrim                = require("enyo-ui/Scrim"),
    Spinner              = require("app/ui/Spinner"),
    ViewContainerSupport = require("app/core/ViewContainerSupport"),
    browser              = require("bowser").getParser(window.navigator.userAgent),
    isIosApp             = require("app/utils/isIosApp"),
    isShittyIOS          = false,
    kind                 = require("enyo/kind"),
    master               = require("enyo/master"),
    osName               = browser.getOSName().toLocaleLowerCase();

require("./LayoutView.less");

// For some reason, safari only fails to provide correct screen resolution in
// IOS10, but other browsers encounter this problem right up until at least
// IOS14.4.1. Unfortunately this also includes iPads that report running
// MacOS... so, for safety we'll just apply the fix for anything running IOS or
// MacOS.
isShittyIOS = Boolean(osName === "macos" || osName === "ios" || isIosApp);

module.exports = kind(/** @lends  app.AppLayout.LayoutView.prototype */ {

    /**
     * @private
     */
    name: "app.AppLayout.LayoutView",

    /**
     * @private
     */
    classes: "app-app-layout-layout-view",

    /**
     * @private
     */
    fit: false,

    /**
     * The region containing the application's header.
     *
     * @name  .$.headerRegion
     * @type  {enyo.Control}
     * @public
     */

    /**
     * The region containing the application's main content region. Page views
     * are meant to be rendered into this layer.
     *
     * @name  .$.contentRegion
     * @type  {enyo.Control}
     * @public
     */

    /**
     * The popup used to show media details.
     *
     * @name  .$.mediaDetailsPopup
     * @type  {app.AppLayout.MediaDetailsPopup}
     * @public
     */

    /**
     * The loading scrim is stacked on top of the contentRegion, but below
     * contentPopup. The loading should be visible and block interactions with
     * the content region.
     *
     * @name  .$.loadingScrim
     * @type  {enyo-ui.Scrim}
     * @public
     */

    /**
     * @private
     */
    loadingGraphicRequests: 0,

    /**
     * The interaction scrim is stacked on top of the contentRegion as well as
     * the contentPopup. It's main purpose is to block any interaction with the
     * content area's while header dropdowns are open. This allows the user to
     * click outside of an opened dropdown menu to close it, without also
     * interacting with the underlying content.
     *
     * @name  .$.interactionScrim
     * @type  {enyo-ui.Scrim}
     * @public
     */

    /**
     * @private
     */
    interactionScrimRequests: 0,

    /**
     * @private
     */
    scrollPreventionNode: null,

    /**
     * @private
     */
    iosResizeEvent: null,

    /**
     * @private
     */
    iosResizeTimeout: null,

    /**
     * @private
     */
    iosResizeWait: null,

    /**
     * @private
     */
    iosResizeMaxWait: 2500,

    /**
     * @private
     */
    iosResizeWidth: null,

    /**
     * @private
     */
    iosResizeHeight: null,

    /**
     * @private
     */
    components: [{
        kind:        Background,
        classes:     "app-app-layout-layout-view__background",
        canGenerate: false
    }, {
        name:    "headerRegion",
        mixins:  [ViewContainerSupport],
        classes: "app-app-layout-layout-view__header-region"
    }, {
        name:    "contentRegion",
        mixins:  [ViewContainerSupport],
        classes: "app-app-layout-layout-view__content-region"
    }, {
        name:               "loadingScrim",
        kind:               Scrim,
        floating:           false,
        classes:            "app-app-layout-layout-view__loading-scrim app-ui-scrim",
        showing:            false,
        initialOpacity:     0,
        usePerformanceMode: true
    }, {
        name:               "loadingSpinner",
        kind:               Spinner,
        classes:            "app-app-layout-layout-view__loading-spinner",
        mixins:             [FadeSupport],
        showing:            false,
        initialOpacity:     0,
        usePerformanceMode: true
    }, {
        name:           "interactionScrim",
        kind:           Scrim,
        floating:       false,
        classes:        "app-app-layout-layout-view__interaction-scrim",
        showing:        false,
        initialOpacity: 0
    }, {
        name:        "mediaDetailsPopup",
        kind:        MediaDetailsPopup,
        classes:     "app-app-layout-layout-view__media-details-popup",
        canGenerate: false
    }, {
        name:   "mediaPlayerRegion",
        mixins: [
            FloatingSupport,
            ViewContainerSupport
        ],
        classes:  "app-app-layout-layout-view__media-player-region enyo-fit",
        floating: true
    }],

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.shittyIOSResize = this.bindSafely(this.shittyIOSResize);
        };
    }),

    /**
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            var layoutNode    = this.hasNode(),
                floatingLayer = Control.floatingLayer,
                floatNode     = floatingLayer.hasNode();

            // Ensure floatingLayer is rendered
            if (!floatNode) {
                floatingLayer.render();
                floatNode = floatingLayer.hasNode();
            }

            // The layout has it's own stacking context due to it's fixed
            // position. By moving the floatingLayer's node into the layout we
            // place it in the same stacking context as the rest of the app
            // layout. This allows us to use z-index to make dropdown menu's
            // display on top of content in the floatingLayer.
            if (floatNode.parentNode !== layoutNode) {
                floatNode.parentNode.removeChild(floatNode);
                layoutNode.appendChild(floatNode);
            }

            // Use height of 100vh in vikaru-ios-app
            if (isIosApp) {
                document.documentElement.classList.add("vikaru-app-ios");
            }

            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    handleResize: kind.inherit(function(sup) {
        return function(sender, event) {
            var node          = this.hasNode(),
                shittyIOSMode = isShittyIOS && node;

            // IOS < 11 doesn't actually provide the correct window size. It can
            // take ~300ms and sometimes up to 1000ms before the correct
            // dimensions are returned. To fix this, we memorize the current
            // size of the layout and if another size is encountered later, we
            // trigger a 2nd resize cycle.
            if (shittyIOSMode) {
                // Clear any existing timeout
                this.iosResizeTimeout && clearTimeout(this.iosResizeTimeout);
                this.iosResizeWait = 0;

                // Memorize layout dimensions
                this.iosResizeWidth  = node.offsetWidth;
                this.iosResizeHeight = node.offsetHeight;
            }

            // The original reflow
            sup.apply(this, arguments);

            // And here's that 2nd resize cycle mentioned above.
            if (shittyIOSMode) {
                this.iosResizeEvent   = event;
                this.iosResizeTimeout = setTimeout(this.shittyIOSResize, 100);
            }
        };
    }),

    /**
     * @private
     */
    shittyIOSResize: function() {
        var node           = this.hasNode(),
            iosResizeEvent = this.iosResizeEvent;

        this.iosResizeTimeout = null;

        // Nothing to do here.
        if (!node || !isShittyIOS || !iosResizeEvent) {
            return;
        }

        // Update waited time
        this.iosResizeWait += 100;

        // Layout dimensions changed, trigger 2nd resize cycle
        if (this.iosResizeWidth !== node.offsetWidth
            || this.iosResizeHeight !== node.offsetHeight) {
            master.bubble("onresize", iosResizeEvent);
        }

        // Layout dimensions didn't change yet, but we're willing to wait a bit
        // longer.
        else if (this.iosResizeWait < this.iosResizeMaxWait) {
            this.iosResizeTimeout = setTimeout(this.shittyIOSResize, 100);
        }

        // If dimensions were going to change, that should've happened by now.
        else {
            this.iosResizeEvent = null;
        }
    },

    /**
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var windowWidth,
                windowHeight;

            // We remove dimensions to let the browser determine the window's
            // inner dimensions.
            document.body.style.width  = null;
            document.body.style.height = null;

            // Now we re-apply the window's dimensions to the layout. This
            // should ensure the layout is exactly the right size. For some
            // reason this works better than relying on css, in particularly on
            // mobile browsers where some of the browser chrome is only
            // sometimes visible.
            windowWidth                = window.innerWidth + "px";
            windowHeight               = window.innerHeight + "px";
            document.body.style.width  = windowWidth;
            document.body.style.height = windowHeight;
            sup.apply(this, arguments);
            this.set("headerRegionBounds", this.$.headerRegion.getAbsoluteBounds());
            this.set("contentRegionBounds", this.$.contentRegion.getAbsoluteBounds());
        };
    }),

    // Loading Graphic
    // --------------------------------

    /**
     * Request that the loading graphic gets shown.
     *
     * @public
     */
    requestShowLoadingGraphic: function() {
        this.set("loadingGraphicRequests", this.loadingGraphicRequests + 1);
    },

    /**
     * Request that the loading graphic gets hidden.
     *
     * @public
     */
    requestHideLoadingGraphic: function() {
        this.set("loadingGraphicRequests", this.loadingGraphicRequests - 1);
    },

    /**
     * Shows the loading graphic if it was requested at least once. Hides the
     * loading graphic again once all previous requests have been withdrawn.
     *
     * @param {number}  oldCount  The previous amount of requests for a
     *                            loading graphic.
     * @private
     */
    loadingGraphicRequestsChanged: function(oldCount) {
        var currentCount   = this.loadingGraphicRequests,
            loadingScrim   = this.$.loadingScrim,
            loadingSpinner = this.$.loadingSpinner;

        if (currentCount > 0 && oldCount === 0) {
            loadingScrim.requestShow(true, false);
            loadingSpinner.show(true, false);
        }

        if (currentCount === 0 && oldCount > 0) {
            loadingScrim.requestHide(true, false);
            loadingSpinner.hide(true, false);
        }
    },

    // Interaction Scrim
    // --------------------------------

    /**
     * Request that the interaction scrim gets shown, blocking any interaction
     * with the content region or content popups.
     *
     * @public
     */
    blockContentInteraction: function() {
        this.set("interactionScrimRequests", this.interactionScrimRequests + 1);
    },

    /**
     * Request that the interaction scrim gets shown, removing the block on
     * interaction with the content region or content popups.
     *
     * @public
     */
    unblockContentInteraction: function() {
        this.set("interactionScrimRequests", this.interactionScrimRequests - 1);
    },

    /**
     * Shows the interaction scrim if it was requested at least once. Hides the
     * interaction scrim again once all previous requests have been withdrawn.
     *
     * @param {number}  oldCount  The previous amount of requests for an
     *                            interaction scrim.
     * @private
     */
    interactionScrimRequestsChanged: function(oldCount) {
        var currentCount     = this.interactionScrimRequests,
            interactionScrim = this.$.interactionScrim;

        if (currentCount > 0 && oldCount === 0) {
            interactionScrim.requestShow();
        }

        if (currentCount === 0 && oldCount > 0) {
            interactionScrim.requestHide();
        }
    }

});
